// client/src/pages/admin/ConciergeOnboarding.js
// ============================================
// CONCIERGE ONBOARDING — LIST PAGE
// Lists all concierge onboarding batches with status + actions.
// ============================================

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Plus, Trash2, ArrowRight } from 'lucide-react';
import { adminOnboardingAPI } from '../../services/api';
import ConfirmModal from '../../components/common/ConfirmModal';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const statusColor = {
  draft: 'bg-gray-100 text-gray-700',
  processing: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  partially_failed: 'bg-amber-100 text-amber-800'
};

const formatDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString();
  } catch {
    return String(d);
  }
};

const ConciergeOnboarding = () => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminOnboardingAPI.listBatches();
      const data = res.data?.batches || res.data?.data || res.data || [];
      setBatches(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = (batch) => {
    setConfirm({
      title: t('conciergeOnboarding.confirm.deleteBatch.title', 'Delete batch?'),
      message: t('conciergeOnboarding.confirm.deleteBatch.message', 'This will remove the batch and all its records. Processed accounts are NOT affected.'),
      onConfirm: async () => {
        try {
          await adminOnboardingAPI.deleteBatch(batch._id);
          toast.success(t('conciergeOnboarding.toasts.deleted', 'Batch deleted'));
          setConfirm(null);
          load();
        } catch (err) {
          toast.error(err.response?.data?.message || 'Failed to delete');
        }
      }
    });
  };

  const rows = useMemo(() => batches, [batches]);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('conciergeOnboarding.title', 'Concierge Onboarding')}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {t('conciergeOnboarding.subtitle', 'Create business accounts on behalf of wholesalers.')}
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/onboarding/new')}
            className="inline-flex items-center gap-2 rounded-md bg-indigo-600 text-white px-4 py-2 text-sm font-medium hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" />
            {t('conciergeOnboarding.newBatch', 'New batch')}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-10 text-center">
            <p className="text-gray-600">
              {t('conciergeOnboarding.empty.title', 'No onboarding batches yet.')}
            </p>
            <button
              onClick={() => navigate('/admin/onboarding/new')}
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-indigo-600 text-white px-4 py-2 text-sm font-medium hover:bg-indigo-700"
            >
              <Plus className="w-4 h-4" />
              {t('conciergeOnboarding.newBatch', 'New batch')}
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('conciergeOnboarding.listColumns.wholesaler', 'Wholesaler')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('conciergeOnboarding.listColumns.records', 'Records')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('conciergeOnboarding.listColumns.status', 'Status')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('conciergeOnboarding.listColumns.branding', 'Branding')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('conciergeOnboarding.listColumns.created', 'Created')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {t('conciergeOnboarding.listColumns.actions', 'Actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((b) => {
                  const wsName =
                    b.wholesaler?.companyName ||
                    b.wholesaler?.wholesalerName ||
                    b.wholesaler?.email ||
                    '—';
                  const invited = b.counts?.invited ?? (b.records || []).filter((r) => r.status === 'invited').length;
                  const total = b.counts?.total ?? (b.records || []).length;
                  return (
                    <tr key={b._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{wsName}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {invited}/{total}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${statusColor[b.status] || 'bg-gray-100 text-gray-700'}`}>
                          {t(`conciergeOnboarding.statusChips.${b.status}`, b.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {t(`conciergeOnboarding.brandingChips.${b.emailBranding || 'wholesaler'}`, b.emailBranding || 'wholesaler')}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(b.createdAt)}</td>
                      <td className="px-4 py-3 text-sm text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/admin/onboarding/${b._id}`)}
                            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800"
                          >
                            {t('conciergeOnboarding.open', 'Open')}
                            <ArrowRight className="w-4 h-4" />
                          </button>
                          {b.status === 'draft' && (
                            <button
                              onClick={() => handleDelete(b)}
                              className="text-red-600 hover:text-red-800"
                              title={t('conciergeOnboarding.delete', 'Delete')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        icon="danger"
        confirmColor="red"
        onConfirm={confirm?.onConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default ConciergeOnboarding;
